import { ConfigurationType, ExistingSystemType, LevelType } from 'data/types';
import {
  getDuctedPairIncompleteReason,
  getDuctedPairLinks,
  getDuctedPairZone,
  getUnpairedDuctedSystemIds
} from 'utils/ducted-design';
import {
  DuctedPairIncompleteReason,
  DuctedPairLinkMode
} from 'utils/ducted-design-types';
import {
  DuctedPairStatus,
  DuctedPairSummary
} from 'utils/ducted-pair-summary-types';
import { findLevelForSpace } from 'utils/grid';
import { calculateZoneTotalBTUs } from 'utils/zone-outline';

const INCOMPLETE_REASON_COPY: Record<DuctedPairIncompleteReason, string> = {
  unpositioned: 'Place this condenser on the floor plan.',
  'no-zone': 'Give this pair its rooms by adding a zone.',
  'multiple-zones':
    'A ducted pair serves one zone — merge or remove the extra zones.',
  'no-air-handler': 'Place a ducted air handler in this zone.',
  unlinked: 'Link this pair to one of your existing ducted systems.'
};

const LINKING_COPY = "Preparing this system's recommendations...";

const UNNAMED_SYSTEM_LABEL = 'Existing System';

const systemList = new Intl.ListFormat('en', {
  style: 'long',
  type: 'conjunction'
});

const ZONELESS_REASONS: ReadonlySet<DuctedPairIncompleteReason> = new Set([
  'no-zone',
  'multiple-zones'
]);

export function getDuctedPairStatus(
  incompleteReason: DuctedPairIncompleteReason | null,
  linkMode: DuctedPairLinkMode
): DuctedPairStatus {
  if (incompleteReason === null) return { kind: 'ready' };
  if (incompleteReason === 'unlinked' && linkMode === 'automatic') {
    return { kind: 'linking' };
  }
  return { kind: 'incomplete', reason: incompleteReason };
}

export function describeDuctedPairStatus(
  status: DuctedPairStatus
): string | null {
  if (status.kind === 'ready') return null;
  if (status.kind === 'linking') return LINKING_COPY;
  return INCOMPLETE_REASON_COPY[status.reason];
}

export function isDuctedPairLinkable(status: DuctedPairStatus): boolean {
  return !(status.kind === 'incomplete' && ZONELESS_REASONS.has(status.reason));
}

export function getDuctedPairTarget(
  pairCount: number,
  eligibleSystemCount: number,
  linkMode: DuctedPairLinkMode
): number {
  return linkMode === 'manual'
    ? Math.max(pairCount, eligibleSystemCount)
    : pairCount;
}

export function describeUnpairedDuctedSystems(
  configuration: Pick<ConfigurationType, 'condensers'>,
  eligibleSystems: readonly ExistingSystemType[],
  linkMode: DuctedPairLinkMode
): string | null {
  if (linkMode !== 'manual') return null;
  const unpairedIds = new Set(
    getUnpairedDuctedSystemIds(
      configuration,
      new Set(eligibleSystems.map((system) => system.uniqueId))
    )
  );
  const unlinkedPairCount =
    configuration.condensers.length - getDuctedPairLinks(configuration).length;
  if (unpairedIds.size <= unlinkedPairCount) return null;
  const names = eligibleSystems
    .filter((system) => unpairedIds.has(system.uniqueId))
    .map((system) => system.name || UNNAMED_SYSTEM_LABEL);
  if (names.length === 1) {
    return `${names[0]} still needs a ducted pair — place a condenser and air handler for it, then link the pair.`;
  }
  return `${systemList.format(
    names
  )} still need ducted pairs — place a condenser and air handler for each, then link the pairs.`;
}

export function buildDuctedPairSummaries(
  configuration: ConfigurationType,
  structure: LevelType[],
  eligibleSystemIds: ReadonlySet<string>,
  linkMode: DuctedPairLinkMode
): DuctedPairSummary[] {
  return configuration.condensers.map((condenser) => {
    const zone = getDuctedPairZone(condenser);
    const zoneItems = zone?.zoneItems ?? [];
    const loads = zone
      ? calculateZoneTotalBTUs(zone)
      : { heating: 0, cooling: 0 };
    const levelLabels = Array.from(
      new Set(
        zoneItems
          .map((item) => findLevelForSpace(item.structureItemId, structure))
          .filter((level): level is LevelType => level !== null)
          .map((level) => level.label)
      )
    );
    return {
      condenserId: condenser.uniqueId,
      pairName: condenser.name,
      zoneName: zone?.name ?? null,
      existingSystemId: zone?.existingSystemId ?? null,
      levelLabels,
      roomCount: zoneItems.length,
      coolingBtu: loads.cooling,
      heatingBtu: loads.heating,
      status: getDuctedPairStatus(
        getDuctedPairIncompleteReason(condenser, eligibleSystemIds),
        linkMode
      )
    };
  });
}
